"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.dateUtilities = void 0;
var constants_1 = require("../constants/constants");
var dayjs_1 = __importDefault(require("dayjs"));
var advancedFormat_1 = __importDefault(require("dayjs/plugin/advancedFormat"));
var timezone_1 = __importDefault(require("dayjs/plugin/timezone"));
var utc_1 = __importDefault(require("dayjs/plugin/utc"));
dayjs_1.default.extend(advancedFormat_1.default);
dayjs_1.default.extend(timezone_1.default);
dayjs_1.default.extend(utc_1.default);
exports.dateUtilities = {
    /**
     * Returns the time with the format "14:30" It uses the locale timezone.
     * @param timeStamp number. The timestamp of the time to be converted.
     * @param timeZone string. The time zone name
     * @returns string. The time string formatted in 24 hour format.
     */
    formatTime: function (timeStamp, timeZone) {
        if (timeZone === void 0) { timeZone = null; }
        var date = exports.dateUtilities.getDateForTimeZone(new Date(timeStamp), timeZone);
        var time = timeStamp ? ('0' + date.getHours()).slice(-2) + ":" + ('0' + date.getMinutes()).slice(-2) : '00:00';
        return time;
    },
    /**
     * Returns true if timeZone string is recognized
     * @param timeZone string. The time zone name
     * @returns boolean. The result of time zone validation
     */
    isValidTimeZone: function (timeZone) {
        try {
            Intl.DateTimeFormat(undefined, { timeZone: timeZone });
            return true;
        }
        catch (ex) {
            return false;
        }
    },
    /**
     * Returns the time in the 12-hour format. Example - 12:24am
     * @param timeStamp number. The timestamp of the time to be converted
     * @returns string. The time string formatted in 12 hour format
     */
    formatTwelveHourTime: function (timeStamp, timeZone) {
        if (timeZone === void 0) { timeZone = null; }
        var date = exports.dateUtilities.getDateForTimeZone(new Date(timeStamp), timeZone);
        var hours = date.getHours();
        var hoursToShow = hours % 12;
        // HoursToShow can be '0' which will result in showing '12'
        return (hoursToShow ? hoursToShow : 12) + ":" + ('0' + date.getMinutes()).slice(-2) + (hours >= 12 ? 'pm' : 'am');
    },
    /**
     * Returns the date with the format "10 February 14:30" It uses the locale timezone.
     * @param timeStamp number. The timestamp of the time to be converted.
     * @param t The translation object used to translate the month name.
     * @returns string. The date string formatted in {day} {month} {hours}:{minutes} format.
     */
    formatDate: function (timeStamp, t) {
        var date = new Date(timeStamp);
        var formatter = new Intl.DateTimeFormat(window.navigator.language, { month: 'long' });
        var month = formatter.format(date).replace('.', '');
        var time = ('0' + date.getHours()).slice(-2) + ":" + ('0' + date.getMinutes()).slice(-2);
        return date.getDate() + " " + t(month.toLocaleLowerCase()) + " " + time;
    },
    /**
     * Returns the date with the format "Jun 2" or "Today".
     * @param timeStamp number. The timestamp of the date to be converted.
     * @returns string. The date string formatted in {short month} {day} format (or Today if it it the current date).
     */
    formatDateMonthDay: function (timeStamp, useUTC) {
        if (useUTC === void 0) { useUTC = false; }
        if (useUTC) {
            var date_1 = new Date(timeStamp);
            return constants_1.shortMonths[date_1.getUTCMonth()] + " " + date_1.getUTCDate() + " (UTC)";
        }
        var timeShift = process.env.TIMEZONE_SHIFT || '0';
        timeStamp += parseInt(timeShift, 10);
        var date = new Date(timeStamp);
        return exports.dateUtilities.isToday(date) ? 'Today' : constants_1.shortMonths[date.getMonth()] + " " + date.getDate();
    },
    /**
     * Returns the date with the format "14 Dec. 2020".
     * @param timeStamp number. The timestamp of the date to be converted.
     * @returns string. The date string formatted in {day} {short month}. {year} format.
     */
    formatDateDayShortMonthYear: function (timeStamp) {
        var timeShift = process.env.TIMEZONE_SHIFT || '0';
        timeStamp += parseInt(timeShift, 10);
        var date = new Date(timeStamp);
        return date.getDate() + " " + constants_1.shortMonths[date.getMonth()] + ". " + date.getFullYear();
    },
    /**
     * Checks whether or not the passed in date is the current day's date.
     * @param currentDate Date. The date to check.
     * @returns boolean. A flag to indicate whether or not the passed in date matches the current day's date.
     */
    isToday: function (currentDate) {
        var today = new Date();
        return currentDate.getDate() === today.getDate() && currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();
    },
    /**
     * Formats the given timestamp in "2020 4 2" format.
     * @param timeStamp number. The timestamp of the date to be converted.
     * @returns string. The date string in the format {year} {month number} {day}
     */
    formatDateYearMonthDay: function (timeStamp) {
        var timeShift = process.env.TIMEZONE_SHIFT || '0';
        timeStamp += parseInt(timeShift, 10);
        var date = new Date(timeStamp);
        return date.getFullYear() + " " + date.getMonth() + " " + date.getDate();
    },
    /**
     * Formats the given timestamp into a string that contains the day name and the time in hours and minutes.
     * @param timeStamp number. The timestamp of the date to be converted.
     * @param useShortDays boolean (optional). A flag to indicate whether or not to use short name for the day, e.g. Mon instead of Monday.
     * @param separator boolean (optional). A separator to use between day and time. Separator used by default ' - '.
     * @param timeZone string (optional). The time zone name
     * @returns string. A string in the format {day} - {hours}:{minutes}
     */
    formatDateTime: function (timeStamp, useShortDays, separator, timeZone) {
        if (useShortDays === void 0) { useShortDays = false; }
        if (separator === void 0) { separator = ' - '; }
        if (timeZone === void 0) { timeZone = null; }
        var timeShift = process.env.TIMEZONE_SHIFT || '0';
        timeStamp += parseInt(timeShift, 10);
        var date = exports.dateUtilities.getDateForTimeZone(new Date(timeStamp), timeZone);
        var day = '';
        switch (date.getDay()) {
            case 0:
                day = useShortDays ? 'Sun' : 'Sunday';
                break;
            case 1:
                day = useShortDays ? 'Mon' : 'Monday';
                break;
            case 2:
                day = useShortDays ? 'Tue' : 'Tuesday';
                break;
            case 3:
                day = useShortDays ? 'Wed' : 'Wednesday';
                break;
            case 4:
                day = useShortDays ? 'Thu' : 'Thursday';
                break;
            case 5:
                day = useShortDays ? 'Fri' : 'Friday';
                break;
            case 6:
                day = useShortDays ? 'Sat' : 'Saturday';
                break;
            default:
                day = 'Day';
        }
        var tempHours = date.getHours();
        var hours = tempHours === 0 ? '00' : tempHours < 10 ? "0" + tempHours : tempHours;
        var tempMinutes = date.getMinutes();
        var minutes = tempMinutes === 0 ? '00' : tempMinutes < 10 ? "0" + tempMinutes : tempMinutes;
        return "" + day + separator + hours + ":" + minutes;
    },
    /**
     * Formats the given timestamp into "2/4/2020" format, optionally add time with 15:00 format.
     * @param timeStamp number. The timestamp of the date to be converted.
     * @param withTime boolean. Default false, add hours and minutes after date in 15:00 format.
     * @returns string. The date string in the format {day}/{month number}/{year} optionally add time with {time} formator empty string if no timestamp provided.
     */
    formatDateDayMonthYear: function (timeStamp, withTime) {
        if (withTime === void 0) { withTime = false; }
        if (!timeStamp) {
            return '';
        }
        var timeShift = process.env.TIMEZONE_SHIFT || '0';
        timeStamp += parseInt(timeShift, 10);
        var date = new Date(timeStamp);
        if (withTime) {
            var tempHours = date.getHours();
            var hours = tempHours < 10 ? "0" + tempHours : tempHours;
            var tempMinutes = date.getMinutes();
            var minutes = tempMinutes < 10 ? "0" + tempMinutes : tempMinutes;
            return date.getDate() + "/" + (date.getMonth() + 1) + "/" + date.getFullYear() + " " + hours + ":" + minutes;
        }
        else {
            return date.getDate() + "/" + (date.getMonth() + 1) + "/" + date.getFullYear();
        }
    },
    /**
     * Returns the day nth with the format "th"
     * @param dayNumber
     * @returns st, nd, rd or th
     */
    getNth: function (dayNumber) {
        if (dayNumber > 3 && dayNumber < 21)
            return 'th';
        switch (dayNumber % 10) {
            case 1:
                return 'st';
            case 2:
                return 'nd';
            case 3:
                return 'rd';
            default:
                return 'th';
        }
    },
    /**
     * Returns countdown to given start time in the format '2d 3h 4m 23s'
     * @param startTime number. The timestamp to be converted
     * @returns string. '2d 3h 4m 23s'
     */
    getFormattedCountdown: function (startTime) {
        startTime = startTime - Date.now();
        var days = Math.floor(startTime / (1000 * 60 * 60 * 24));
        var hours = Math.floor((startTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        var minutes = Math.floor((startTime % (1000 * 60 * 60)) / (1000 * 60));
        var seconds = Math.floor((startTime % (1000 * 60)) / 1000);
        if (startTime < 0) {
            return '';
        }
        else {
            return (days === 0 ? '' : days + 'd') + " " + (hours === 0 && days === 0 ? '' : hours + 'h') + " " + (minutes === 0 && hours === 0 && days === 0 ? '' : minutes + 'm') + " " + seconds + "s";
        }
    },
    /**
     * Returns a string when given a start and end time.
     * In the formats : 1st May - 2nd June given a non matching start and end time;
     *                  1st - 2nd May if the months match;
     *                  1st May if the month and date match.
     * @param startTime number
     * @param endTime number
     * @returns string. '10th-14th December'
     */
    getDateRange: function (startTime, endTime) {
        var formatter = new Intl.DateTimeFormat(window.navigator.language, { month: 'long' });
        var startDate = new Date(startTime);
        var startMonth = formatter.format(startDate).replace('.', '');
        var endDate = new Date(endTime);
        var endMonth = formatter.format(endDate).replace('.', '');
        var endOfString = "" + endDate.getDate() + exports.dateUtilities.getNth(endDate.getDate()) + " " + endMonth;
        var startOfString = "" + startDate.getDate() + exports.dateUtilities.getNth(startDate.getDate());
        var sameMonthString = startOfString + " - " + endOfString;
        var fullString = startOfString + " " + startMonth + " - " + endOfString;
        if (startMonth === endMonth && startDate.getDate() === endDate.getDate()) {
            return endOfString;
        }
        else if (startMonth === endMonth) {
            return sameMonthString;
        }
        else {
            return fullString;
        }
    },
    /**
     * Formats the given timestamp into "1st January" format.
     * @param timeStamp number. The timestamp of the date to be converted.
     * @returns string. The date string in the format {day} {month long name}.
     */
    formatDateDayMonthName: function (timeStamp) {
        var timeShift = process.env.TIMEZONE_SHIFT || '0';
        timeStamp += parseInt(timeShift, 10);
        var date = new Date(timeStamp);
        return "" + date.getDay() + this.getNth(date.getDay()) + " " + constants_1.monthNames[date.getMonth()];
    },
    /**
     * returns a format string according to a language country code
     * @param languageCode DateLocale. The key matching the desired date format
     * @returns string. The date string that creates the desired date format
     */
    getLocaleDateString: function (languageCode) {
        return constants_1.dateFormats[languageCode];
    },
    /**
     * Formats the given timestamp into the supplied format
     * @param timeStamp number. The timestamp of the date to be converted.
     * @param format string. The format which the timestamp is to be returned in
     * @param locale string. The language to translate the date string into
     * @returns string. The date string in the requested format
     */
    customFormatDate: function (timestamp, format, locale) {
        if (locale === void 0) { locale = 'en'; }
        return dayjs_1.default(timestamp).locale(locale).format(format);
    },
    /**
     * Returns the date with the format "Mar. 30th - 12:24pm".
     * @param timeStamp number. The timestamp of the date to be converted.
     * @returns string. The date string formatted in {short month}. {day} - {time} format.
     */
    formatDateMonthDayTime: function (timeStamp) {
        var timeShift = process.env.TIMEZONE_SHIFT || '0';
        timeStamp += parseInt(timeShift, 10);
        var date = new Date(timeStamp);
        return constants_1.shortMonths[date.getMonth()] + ". " + date.getDate() + this.getNth(date.getDate()) + " - " + date.getHours() + "." + ('0' + date.getMinutes()).slice(-2) + (date.getHours() >= 12 ? 'pm' : 'am');
    },
    /**
     * Returns the formatted time value, padded with a zero in front if it's a single digit.
     * @param time number. The value of seconds/minutes/hours/days in UTC format.
     * @returns string. The formatted time value padded with a zero if it's a single digit.
     */
    formatTimeValue: function (time) {
        return time.toString().padStart(2, '0');
    },
    getDateForTimeZone: function (date, timeZone) {
        if (timeZone === void 0) { timeZone = null; }
        if (timeZone && exports.dateUtilities.isValidTimeZone(timeZone)) {
            return new Date(date.toLocaleString('en-US', {
                timeZone: timeZone
            }));
        }
        return date;
    },
    /**
     * Returns the date with the format "Sat 7 Jan 14:00"
     * @param timestamp number. The timestamp of the date to be converted
     * @param timeZone string. The time zone name
     * @returns string. The date string in the requested format
     */
    formatDateDayMonthTime: function (timestamp, timeZone) {
        if (timeZone === void 0) { timeZone = null; }
        var d = exports.dateUtilities.getDateForTimeZone(new Date(timestamp), timeZone);
        return constants_1.shortDays[d.getDay()] + " " + d.getDate() + " " + constants_1.shortMonths[d.getMonth()] + " " + exports.dateUtilities.formatTime(timestamp, timeZone);
    }
};
